import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Card, CardContent, Chip, Divider, Grid } from '@mui/material';
import { jobDescriptionService } from '../../services/jobDescriptionService';
import { COLORS } from '../../config/colors';

interface JobDescriptionContentProps {
  candidate?: any;
  candidateId?: string;
  onContentChange?: (contentId: string) => void;
  onActionClick?: (action: string, data?: any) => void;
}

const JobDescriptionContent: React.FC<JobDescriptionContentProps> = ({ 
  candidate, 
  candidateId, 
  onContentChange, 
  onActionClick 
}) => {
  const [jobData, setJobData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchJobData = async () => {
      // Get job_id from candidate object
      const jobId = candidate?.job_id || candidate?.jobId;
      
      if (!jobId) {
        console.log('[JobDescriptionContent] No job ID available');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log('[JobDescriptionContent] Fetching job data for ID:', jobId);
        const response = await jobDescriptionService.getById(jobId);
        
        setJobData(response);
        console.log('[JobDescriptionContent] Job data fetched successfully:', response);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch job description data';
        setError(errorMessage);
        console.error('[JobDescriptionContent] Error fetching job data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobData();
  }, [candidate, candidateId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontFamily: 'var(--font-heading)', color: 'var(--primary-blue)', mb: 2 }}>
        📋 Job Description
      </Typography>
      <Typography sx={{ fontFamily: 'var(--font-body)', color: 'var(--dark-gray)', mb: 3 }}>
        Comprehensive job description details and requirements.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {jobData && (
        <Card sx={{ mb: 3, border: '1px solid #e0e7ff', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontFamily: 'var(--font-heading)', color: 'var(--primary-blue)', mb: 3 }}>
              📄 Job Details
            </Typography>
            
            {/* Basic Information */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Job Title
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'var(--primary-blue)', fontWeight: 600 }}>
                    {jobData.jobTitle || jobData.title || jobData.job_title || 'Not specified'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Job ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', color: 'var(--primary-blue)' }}>
                    {jobData.jobId || jobData.job_id || jobData.id || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Reference Code
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {jobData.jobReferenceCode || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Job Type
                  </Typography>
                  <Typography variant="body1">
                    {jobData.jobType || jobData.employment_type || 'Not specified'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Job Category
                  </Typography>
                  <Typography variant="body1">
                    {jobData.jobCategory || 'Not specified'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Department
                  </Typography>
                  <Typography variant="body1">
                    {jobData.department || 'Not specified'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Employment Level
                  </Typography>
                  <Typography variant="body1">
                    {jobData.employmentLevel || jobData.experience_level || 'Not specified'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Industry
                  </Typography>
                  <Typography variant="body1">
                    {jobData.industry || 'Not specified'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Status
                  </Typography>
                  <Chip 
                    label={jobData.jobStatus || jobData.status || 'Open'} 
                    size="small" 
                    sx={{ 
                      mt: 0.5,
                      bgcolor: (jobData.jobStatus || jobData.status || '').toLowerCase() === 'open' ? '#4caf50' : 
                               (jobData.jobStatus || jobData.status || '').toLowerCase() === 'closed' ? '#f44336' : 
                               (jobData.jobStatus || jobData.status || '').toLowerCase() === 'draft' ? '#ff9800' : '#2196f3',
                      color: 'white',
                      fontWeight: 600,
                      textTransform: 'capitalize'
                    }} 
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Number of Openings
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'var(--primary-blue)', fontWeight: 600 }}>
                    {jobData.numberOfOpenings || 'Not specified'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Posting Date
                  </Typography>
                  <Typography variant="body1">
                    {jobData.postingDate ? new Date(jobData.postingDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    }) : 'Not specified'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                    Closing Date
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#f44336', fontWeight: 500 }}>
                    {jobData.closingDate ? new Date(jobData.closingDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    }) : 'Not specified'}
                  </Typography>
                </Grid>

                {jobData.assigneeName && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                      Assignee
                    </Typography>
                    <Typography variant="body1">
                      {jobData.assigneeName} ({jobData.assigneeEmail})
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Job Description */}
            {(jobData.jobDescription || jobData.description || jobData.job_description) && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                    Job Description
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'var(--dark-gray)', whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                    {jobData.jobDescription || jobData.description || jobData.job_description}
                  </Typography>
                </Box>
              </>
            )}

            {/* Location Details */}
            {jobData.location && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                    Location Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                        Work Location Type
                      </Typography>
                      <Chip 
                        label={jobData.location.workLocationType || 'Not specified'} 
                        size="small"
                        sx={{ bgcolor: COLORS.INFO_LIGHT, color: 'var(--primary-blue)', fontWeight: 500 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                        Country
                      </Typography>
                      <Typography variant="body1">
                        {jobData.location.country || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                        State
                      </Typography>
                      <Typography variant="body1">
                        {jobData.location.state || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                        City
                      </Typography>
                      <Typography variant="body1">
                        {jobData.location.city || 'Not specified'}
                      </Typography>
                    </Grid>
                    {jobData.location.zipPostalCode && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Zip/Postal Code
                        </Typography>
                        <Typography variant="body1">
                          {jobData.location.zipPostalCode}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.location.officeAddress && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Office Address
                        </Typography>
                        <Typography variant="body1">
                          {jobData.location.officeAddress}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.location.region && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Region
                        </Typography>
                        <Typography variant="body1">
                          {jobData.location.region}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </>
            )}

            {/* Compensation */}
            {jobData.compensation && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                    Compensation & Benefits
                  </Typography>
                  <Grid container spacing={2}>
                    {(jobData.compensation.salaryMin || jobData.compensation.salaryMax) && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Salary Range
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'var(--primary-blue)', fontWeight: 700 }}>
                          {jobData.compensation.salaryCurrency || '$'}{jobData.compensation.salaryMin?.toLocaleString()} - {jobData.compensation.salaryCurrency || '$'}{jobData.compensation.salaryMax?.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)' }}>
                          {jobData.compensation.salaryType || 'Annual'}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                        Bonus Eligibility
                      </Typography>
                      <Chip 
                        label={jobData.compensation.bonusEligibility ? 'Yes' : 'No'} 
                        size="small"
                        sx={{ 
                          bgcolor: jobData.compensation.bonusEligibility ? COLORS.SUCCESS_LIGHT : COLORS.ERROR_LIGHT, 
                          color: jobData.compensation.bonusEligibility ? COLORS.SUCCESS_DARK : '#c62828',
                          fontWeight: 500
                        }}
                      />
                    </Grid>
                    {jobData.compensation.benefitsDescription && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Benefits
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                          {jobData.compensation.benefitsDescription}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.compensation.otherCompensation && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Other Compensation
                        </Typography>
                        <Typography variant="body1">
                          {jobData.compensation.otherCompensation}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </>
            )}

            {/* Requirements */}
            {jobData.requirements && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                    Requirements
                  </Typography>
                  <Grid container spacing={2}>
                    {jobData.requirements.minimumEducationLevel && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Minimum Education
                        </Typography>
                        <Typography variant="body1">
                          {jobData.requirements.minimumEducationLevel}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.requirements.requiredExperienceYears && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Required Experience
                        </Typography>
                        <Typography variant="body1">
                          {jobData.requirements.requiredExperienceYears} years
                        </Typography>
                      </Grid>
                    )}
                    {jobData.requirements.preferredExperienceYears && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Preferred Experience
                        </Typography>
                        <Typography variant="body1">
                          {jobData.requirements.preferredExperienceYears} years
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  {/* Required Skills */}
                  {jobData.requirements.requiredSkills && jobData.requirements.requiredSkills.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 1 }}>
                        Required Skills
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {jobData.requirements.requiredSkills.map((skill: string, index: number) => (
                          <Chip
                            key={index}
                            label={skill}
                            size="small"
                            sx={{
                              bgcolor: COLORS.INFO_LIGHT,
                              color: 'var(--primary-blue)',
                              fontWeight: 500,
                              '&:hover': {
                                bgcolor: '#bbdefb',
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Preferred Skills */}
                  {jobData.requirements.preferredSkills && jobData.requirements.preferredSkills.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 1 }}>
                        Preferred Skills
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {jobData.requirements.preferredSkills.map((skill: string, index: number) => (
                          <Chip
                            key={index}
                            label={skill}
                            size="small"
                            sx={{
                              bgcolor: '#f3e5f5',
                              color: '#7b1fa2',
                              fontWeight: 500,
                              '&:hover': {
                                bgcolor: '#e1bee7',
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Certifications */}
                  {jobData.requirements.certificationsRequired && jobData.requirements.certificationsRequired.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 1 }}>
                        Certifications Required
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {jobData.requirements.certificationsRequired.map((cert: string, index: number) => (
                          <Chip
                            key={index}
                            label={cert}
                            size="small"
                            sx={{
                              bgcolor: COLORS.WARNING_LIGHT,
                              color: COLORS. SECONDARY_RED,
                              fontWeight: 500
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Languages */}
                  {jobData.requirements.languagesRequired && jobData.requirements.languagesRequired.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 1 }}>
                        Languages Required
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {jobData.requirements.languagesRequired.map((lang: string, index: number) => (
                          <Chip
                            key={index}
                            label={lang}
                            size="small"
                            sx={{
                              bgcolor: COLORS.SUCCESS_LIGHT,
                              color: COLORS.SUCCESS_DARK,
                              fontWeight: 500
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {jobData.requirements.travelRequirements && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                        Travel Requirements
                      </Typography>
                      <Typography variant="body1">
                        {jobData.requirements.travelRequirements}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}

            {/* Company Information */}
            {jobData.company && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                    Company Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                        Company Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {jobData.company.companyName}
                      </Typography>
                    </Grid>
                    {jobData.company.companyId && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Company ID
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                          {jobData.company.companyId}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.company.companyDescription && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          About Company
                        </Typography>
                        <Typography variant="body1">
                          {jobData.company.companyDescription}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.company.companyWebsite && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Website
                        </Typography>
                        <Typography 
                          variant="body1" 
                          component="a" 
                          href={jobData.company.companyWebsite} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ color: 'var(--primary-blue)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {jobData.company.companyWebsite}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </>
            )}

            {/* Application Details */}
            {jobData.applicationDetails && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                    Application Details
                  </Typography>
                  <Grid container spacing={2}>
                    {jobData.applicationDetails.applicationURL && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Application URL
                        </Typography>
                        <Typography 
                          variant="body1" 
                          component="a" 
                          href={jobData.applicationDetails.applicationURL} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ color: 'var(--primary-blue)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {jobData.applicationDetails.applicationURL}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.applicationDetails.applicationEmail && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Application Email
                        </Typography>
                        <Typography variant="body1">
                          {jobData.applicationDetails.applicationEmail}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.applicationDetails.applicationDeadline && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Application Deadline
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#f44336', fontWeight: 500 }}>
                          {new Date(jobData.applicationDetails.applicationDeadline).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.applicationDetails.recruiterName && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Recruiter
                        </Typography>
                        <Typography variant="body1">
                          {jobData.applicationDetails.recruiterName}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.applicationDetails.recruiterEmail && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Recruiter Email
                        </Typography>
                        <Typography variant="body1">
                          {jobData.applicationDetails.recruiterEmail}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.applicationDetails.recruiterPhone && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Recruiter Phone
                        </Typography>
                        <Typography variant="body1">
                          {jobData.applicationDetails.recruiterPhone}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.applicationDetails.applicationInstructions && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Application Instructions
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {jobData.applicationDetails.applicationInstructions}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </>
            )}

            {/* Internal Information */}
            {jobData.internal && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                    Internal Information
                  </Typography>
                  <Grid container spacing={2}>
                    {jobData.internal.approvalStatus && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Approval Status
                        </Typography>
                        <Chip 
                          label={jobData.internal.approvalStatus} 
                          size="small"
                          sx={{ 
                            bgcolor: jobData.internal.approvalStatus === 'Approved' ? COLORS.SUCCESS_LIGHT : COLORS.WARNING_LIGHT, 
                            color: jobData.internal.approvalStatus === 'Approved' ? COLORS.SUCCESS_DARK : COLORS. SECONDARY_RED,
                            fontWeight: 600
                          }}
                        />
                      </Grid>
                    )}
                    {jobData.internal.jobPriority && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Priority
                        </Typography>
                        <Chip 
                          label={jobData.internal.jobPriority} 
                          size="small"
                          sx={{ 
                            bgcolor: jobData.internal.jobPriority === 'High' ? COLORS.ERROR_LIGHT : 
                                     jobData.internal.jobPriority === 'Normal' ? COLORS.INFO_LIGHT : '#f3e5f5',
                            color: jobData.internal.jobPriority === 'High' ? '#c62828' : 
                                   jobData.internal.jobPriority === 'Normal' ? 'var(--primary-blue)' : '#7b1fa2',
                            fontWeight: 500
                          }}
                        />
                      </Grid>
                    )}
                    {jobData.internal.source && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Source
                        </Typography>
                        <Typography variant="body1">
                          {jobData.internal.source}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.internal.createdBy && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Created By
                        </Typography>
                        <Typography variant="body1">
                          {jobData.internal.createdBy}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.internal.createdDate && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Created Date
                        </Typography>
                        <Typography variant="body1">
                          {new Date(jobData.internal.createdDate).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.internal.lastModifiedBy && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Last Modified By
                        </Typography>
                        <Typography variant="body1">
                          {jobData.internal.lastModifiedBy}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.internal.lastModifiedDate && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Last Modified Date
                        </Typography>
                        <Typography variant="body1">
                          {new Date(jobData.internal.lastModifiedDate).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.internal.tags && jobData.internal.tags.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 1 }}>
                          Tags
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {jobData.internal.tags.map((tag: string, index: number) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              sx={{
                                bgcolor: '#fce4ec',
                                color: '#880e4f',
                                fontWeight: 500
                              }}
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}
                    {jobData.internal.notes && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Notes
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {jobData.internal.notes}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </>
            )}

            {/* Advanced Settings */}
            {jobData.advanced && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'var(--primary-blue)', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                    Advanced Settings
                  </Typography>
                  <Grid container spacing={2}>
                    {jobData.advanced.jobFunction && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Job Function
                        </Typography>
                        <Typography variant="body1">
                          {jobData.advanced.jobFunction}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.advanced.shiftType && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Shift Type
                        </Typography>
                        <Typography variant="body1">
                          {jobData.advanced.shiftType}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.advanced.workSchedule && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Work Schedule
                        </Typography>
                        <Typography variant="body1">
                          {jobData.advanced.workSchedule}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.advanced.remoteWorkPolicy && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Remote Work Policy
                        </Typography>
                        <Typography variant="body1">
                          {jobData.advanced.remoteWorkPolicy}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                        Security Clearance Required
                      </Typography>
                      <Chip 
                        label={jobData.advanced.securityClearanceRequired ? 'Yes' : 'No'} 
                        size="small"
                        sx={{ 
                          bgcolor: jobData.advanced.securityClearanceRequired ? COLORS.WARNING_LIGHT : COLORS.SUCCESS_LIGHT, 
                          color: jobData.advanced.securityClearanceRequired ? COLORS. SECONDARY_RED : COLORS.SUCCESS_DARK
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                        Relocation Assistance
                      </Typography>
                      <Chip 
                        label={jobData.advanced.relocationAssistance ? 'Yes' : 'No'} 
                        size="small"
                        sx={{ 
                          bgcolor: jobData.advanced.relocationAssistance ? COLORS.SUCCESS_LIGHT : COLORS.ERROR_LIGHT, 
                          color: jobData.advanced.relocationAssistance ? COLORS.SUCCESS_DARK : '#c62828'
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                        Visa Sponsorship
                      </Typography>
                      <Chip 
                        label={jobData.advanced.visaSponsorship ? 'Yes' : 'No'} 
                        size="small"
                        sx={{ 
                          bgcolor: jobData.advanced.visaSponsorship ? COLORS.SUCCESS_LIGHT : COLORS.ERROR_LIGHT, 
                          color: jobData.advanced.visaSponsorship ? COLORS.SUCCESS_DARK : '#c62828'
                        }}
                      />
                    </Grid>
                    {jobData.advanced.workAuthorizationRequirements && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Work Authorization
                        </Typography>
                        <Typography variant="body1">
                          {jobData.advanced.workAuthorizationRequirements}
                        </Typography>
                      </Grid>
                    )}
                    {jobData.advanced.equalOpportunityStatement && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: 'var(--dark-gray)', fontWeight: 600, mb: 0.5 }}>
                          Equal Opportunity Statement
                        </Typography>
                        <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                          {jobData.advanced.equalOpportunityStatement}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default JobDescriptionContent;

